import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { GuestChip } from "@/components/GuestChip";
import { t } from "@/i18n";

const FILTERS = [
  { id: "all", label: "list.all" },
  { id: "no_rsvp", label: "list.noRsvp" },
  { id: "conflict", label: "list.conflicts" },
  { id: "unseated", label: "list.toDecide" },
];

export const GuestListPanel = ({
  plan,
  conflictIds,
  selectedGuestId,
  draggingGuest,
  onSelectGuest,
  onRequestMove,
  onDragStart,
  onDragEnd,
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const guests = useMemo(() => {
    const list = plan?.guests || [];
    const needle = query.trim().toLowerCase();
    return list.filter((guest) => {
      if (needle && !guest.name.toLowerCase().includes(needle)) {
        const inWords = (guest.blurb || "").toLowerCase().includes(needle);
        const inTags = (guest.interest_tags || []).join(" ").toLowerCase().includes(needle);
        if (!inWords && !inTags) return false;
      }
      if (filter === "no_rsvp") return !guest.responded;
      if (filter === "conflict") return conflictIds.has(guest.id);
      if (filter === "unseated") return !guest.table;
      return true;
    });
  }, [plan, query, filter, conflictIds]);

  return (
    <section className="surface flex min-h-0 flex-col rounded-xl p-4" data-testid="guest-list-panel">
      <header className="mb-2 flex items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold leading-none">{t("list.guests")}</h2>
        <span className="font-num text-[11px] text-[color:var(--muted-ink)]">
          {guests.length}/{(plan?.guests || []).length}
        </span>
      </header>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-[color:var(--muted-ink)]" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("list.search")}
          className="h-9 rounded-lg bg-white pl-8 text-[13px]"
          data-testid="guest-search-input"
        />
      </div>
      <div className="mb-2 flex flex-wrap gap-1">
        {FILTERS.map((item) => (
          <Button
            key={item.id}
            size="sm"
            variant={filter === item.id ? "default" : "outline"}
            className="h-7 rounded-full px-2.5 text-[11px]"
            onClick={() => setFilter(item.id)}
            data-testid={`guest-filter-${item.id}`}
          >
            {t(item.label)}
          </Button>
        ))}
      </div>
      <div className="scroll-thin -mr-1 flex max-h-[560px] min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pr-1">
        {guests.map((guest) => (
          <GuestChip
            key={guest.id}
            guest={guest}
            conflict={conflictIds.has(guest.id)}
            selected={selectedGuestId === guest.id}
            onSelect={onSelectGuest}
            onRequestMove={onRequestMove}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            dragging={draggingGuest?.id === guest.id}
            showTable
          />
        ))}
        {!guests.length ? (
          <p className="py-4 text-center text-[12.5px] text-[color:var(--muted-ink)]">{t("list.noMatch")}</p>
        ) : null}
      </div>
    </section>
  );
};

export default GuestListPanel;
